import { Node, mergeAttributes } from '@tiptap/core';
import { Editor } from '@tiptap/react';
import { ReactNodeViewRenderer } from '@tiptap/react';
import InlineAssetView from '../InlineAssetView';
import { rcsTypes } from 'utility/constants';
import { EditorialAssetSearchResultItem, TRcsTypes } from 'utility/types/dataProvider';

/**
 * Creates a bloxInline node for insertion into the editor.
 * Handles the logic for determining if the asset is related content or a regular asset.
 *
 * @param editor - The Tiptap editor instance
 * @param record - The editorial asset search result item to create a node for
 * @returns The created ProseMirror node, or null if creation fails
 */
export const createBloxInlineNode = (
  editor: Editor,
  record: EditorialAssetSearchResultItem,
) => {
  if (!editor.schema.nodes.bloxInline) {
    console.error(
      '[createBloxInlineNode] bloxInline node type is not available in the editor schema.',
    );
    return null;
  }

  const isRelContent = Boolean(
    record.type && rcsTypes.includes(record.type as TRcsTypes),
  );

  return editor.schema.nodes.bloxInline.create({
    type: isRelContent ? 'relcontent' : 'asset',
    asset_type: record.type,
    uuid: record.uuid || record.id, // Fallback to id if uuid is not available
    app: 'editorial',
    placeholder: false,
  });
};

export interface BloxInlineOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    bloxInline: {
      insertBloxInline: (options: {
        type: string;
        alignment?: string;
        app?: string;
        uuid?: string;
        presentation?: string;
        width?: string;
        placeholder?: boolean;
        html?: string;
        importId?: string;
      }) => ReturnType;
    };
  }
}

export const BloxInline = Node.create<BloxInlineOptions>({
  name: 'bloxInline',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,
  inline: false,
  // Atomic nodes don't have editable content
  // The NodeViewContent in the React component is for React children only

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      //Blox Inline Attributes
      asset_type: {
        default: null,
        parseHTML: (element) => element.getAttribute('asset_type'),
      },
      type: {
        default: null,
        parseHTML: (element) => element.getAttribute('type'),
      },
      alignment: {
        default: '',
        parseHTML: (element) => element.getAttribute('alignment'),
      },
      app: {
        default: null,
        parseHTML: (element) => element.getAttribute('app'),
      },
      uuid: {
        default: null,
        parseHTML: (element) => element.getAttribute('uuid'),
      },
      presentation: {
        default: '',
        parseHTML: (element) => element.getAttribute('presentation'),
      },
      width: {
        default: '',
        parseHTML: (element) => element.getAttribute('width'),
      },
      placeholder: {
        default: true,
        parseHTML: (element) =>
          element.getAttribute('placeholder') === 'true' ? true : false,
      },
      html: {
        default: '',
        parseHTML: (element) => element.getAttribute('html'),
      },
      importId: {
        default: null,
        parseHTML: (element) => element.getAttribute('importId'),
      },
      //CMSADMIN-2081 - Adds in localization asset data support
      'override-title': {
        default: null,
        parseHTML: (element) => element.getAttribute('override-title'),
      },
      'override-byline': {
        default: null,
        parseHTML: (element) => element.getAttribute('override-byline'),
      },
      'override-content': {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute('override-content');
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'blox-inline',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    // Atomic node - renders as empty element with attributes only
    return ['blox-inline', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(InlineAssetView);
  },

  addCommands() {
    return {
      insertBloxInline:
        (options) =>
        ({ chain, state, commands }) => {
          const { selection } = state;
          const { $to, $from } = selection;

          // Explicitly set placeholder to true if not provided
          // Ensure new placeholder nodes do NOT have a uuid
          const attrs = {
            asset_type: options.type, // For counting logic
            type: options.type,
            alignment: options.alignment || '',
            app: options.app || null,
            uuid:
              options.placeholder !== undefined && options.placeholder
                ? undefined
                : options.uuid || null,
            presentation: options.presentation || '',
            width: options.width || '',
            placeholder: options.placeholder !== undefined ? options.placeholder : true,
          };

          const content = {
            type: this.name,
            attrs,
          };

          // Determine insertion position
          // If at the start of a node, insert at current position
          // Otherwise, insert at the end of the current parent
          const insertPos = $from.parentOffset === 0 ? $from.pos : $to.end();

          return chain()
            .insertContentAt(insertPos, content, {
              updateSelection: true,
            })
            .run();
        },
    };
  },
});
